const MONDAY = 1;
const FRIDAY = 5;
// TIMES IN EST (with buffer)
const MARKET_START_HOUR = 9;
const MARKET_END_HOUR = 17; // consider market close at 5 to capture extra data

const marketTimezone = { locale: 'en-US', zoneObject: { timeZone: 'America/New_York' } };

export const nowInMarketTimezoneString = (timestamp?: number) => {
  if (timestamp) {
    return new Date(timestamp).toLocaleString(marketTimezone.locale, marketTimezone.zoneObject);
  } else {
    return new Date().toLocaleString(marketTimezone.locale, marketTimezone.zoneObject);
  }
};

export const nowInMarketTimezoneDate = () => {
  return new Date(nowInMarketTimezoneString());
};

// Cached because Intl formatter construction is the expensive part and this is
// called from render paths that re-run on every live spot tick.
let sessionDateFormatter: Intl.DateTimeFormat | null = null;

/**
 * The market session's calendar day as `YYYY-MM-DD`, always in ET.
 *
 * Use this (not the browser's local date) to scope anything that resets with the
 * trading day — a user in Asia is already on "tomorrow" locally while the US
 * session is still open, and a user west of ET flips over before the close.
 */
export const marketSessionDate = (timestamp?: number): string => {
  if (!sessionDateFormatter) {
    // en-CA formats as YYYY-MM-DD, which sorts and compares as a plain string.
    sessionDateFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  return sessionDateFormatter.format(timestamp ?? Date.now());
};

export const isMarketHours = (timestamp?: number) => {
  const todayNy = nowInMarketTimezoneString(timestamp);
  const d = new Date(todayNy);

  // console.log('todayNy', todayNy, d, new Date().getHours());

  const day = d.getDay();
  const hour = d.getHours();
  const isWeekday = day >= MONDAY && day <= FRIDAY;
  const isAfterOpen = hour >= MARKET_START_HOUR;
  const isBeforeClose = hour <= MARKET_END_HOUR;

  // console.log(d, day, hour);
  // console.log({ isWeekday, isAfterOpen, isBeforeClose, hour });

  return isWeekday && isAfterOpen && isBeforeClose;
};

const MARKET_OPEN_MINUTES = 9 * 60 + 30; // 9:30 AM ET
const MARKET_CLOSE_MINUTES = 16 * 60; // 4:00 PM ET

/** Minutes before the 9:30 AM ET open and after the 4:00 PM ET close. A number applies to both. */
export type MarketHoursBuffer = number | { beforeOpen: number; afterClose: number };

// Weekday window around the regular session, expanded by the buffer on each side.
// e.g. 60 => 8:30 AM ET to 5:00 PM ET; { beforeOpen: 180, afterClose: 60 } => 6:30 AM ET
// to 5:00 PM ET, Mon–Fri.
export const isWithinExtendedMarketHours = (buffer: MarketHoursBuffer, timestamp?: number) => {
  const { beforeOpen, afterClose } =
    typeof buffer === 'number' ? { beforeOpen: buffer, afterClose: buffer } : buffer;
  const d = new Date(nowInMarketTimezoneString(timestamp));
  const day = d.getDay();
  if (day < MONDAY || day > FRIDAY) return false;

  const minutesOfDay = d.getHours() * 60 + d.getMinutes();
  return (
    minutesOfDay >= MARKET_OPEN_MINUTES - beforeOpen &&
    minutesOfDay <= MARKET_CLOSE_MINUTES + afterClose
  );
};
